import { useEffect } from "react";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Demo from "@/components/Demo";
import AriaLive from "@/components/AriaLive";
import Marquee from "@/components/Marquee";
import Product from "@/components/Product";
import Mvp from "@/components/Mvp";
import Status from "@/components/Status";
import WhyDifferent from "@/components/WhyDifferent";
import Characters from "@/components/Characters";
import BusinessModel from "@/components/BusinessModel";
import Market from "@/components/Market";
import Roadmap from "@/components/Roadmap";
import TechDemo from "@/components/TechDemo";
import FounderVideo from "@/components/FounderVideo";
import Investment from "@/components/Investment";
import InvestNow from "@/components/InvestNow";
import FutureVision from "@/components/FutureVision";
import About from "@/components/About";
import Updates from "@/components/Updates";
import Contact from "@/components/Contact";
import TermsOfService from "@/components/legal/TermsOfService";
import PrivacyPolicy from "@/components/legal/PrivacyPolicy";
import FairUsePolicy from "@/components/legal/FairUsePolicy";
import CookiePolicy from "@/components/legal/CookiePolicy";
import ArticlePage from "@/components/ArticlePage";
import StatsDashboard from "@/components/StatsDashboard";
import { ARTICLES } from "@/data/articles";
import { DETAIL_PAGES } from "@/data/detailPages";
import DetailPage from "@/components/DetailPage";
import RegisterPage from "@/components/RegisterPage";
import CookieConsent from "@/components/CookieConsent";
import Footer from "@/components/Footer";

const LEGAL_PAGES = {
  "/terms-of-service": TermsOfService,
  "/privacy-policy": PrivacyPolicy,
  "/fair-use-policy": FairUsePolicy,
  "/cookie-policy": CookiePolicy,
};

const REDIRECTS = {
  "/roadmap/humanoid-ai-robotics": "/roadmap/humanoid-ai",
};

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function App() {
  const legalPath = window.location.pathname.replace(/\/+$/, "");
  const redirect = REDIRECTS[legalPath];
  const LegalPage = LEGAL_PAGES[legalPath];
  const article = legalPath.startsWith("/updates/") ? ARTICLES[legalPath] : undefined;
  const detail = DETAIL_PAGES[legalPath];
  const isStats = legalPath === "/stats";
  const isRegister = legalPath === "/register";

  useEffect(() => {
    if (redirect) window.location.replace(redirect);
  }, [redirect]);

  useEffect(() => {
    if (LegalPage || article || detail || isStats || isRegister) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, [LegalPage, article, isStats, isRegister]);

  useEffect(() => {
    if (LegalPage || article || detail || isStats || isRegister) return;
    const hash = window.location.hash;
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash.slice(1));
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
      else el.scrollIntoView();
    }, 450);
    return () => clearTimeout(t);
  }, [LegalPage, article, detail, isStats, isRegister]);

  if (redirect) return null;
  if (isStats) return <StatsDashboard />;

  let page;
  if (LegalPage) page = <LegalPage />;
  else if (isRegister) page = <RegisterPage />;
  else if (article) page = <ArticlePage article={article} />;
  else if (detail) page = <DetailPage page={detail} />;
  else {
    page = (
      <div className="bg-[#050505] text-white">
        <Navbar />
        <main>
          <Hero />
          <Demo />
          <AriaLive />
          <Marquee />
          <Product />
          <Mvp />
          <Status />
          <WhyDifferent />
          <Characters />
          <BusinessModel />
          <Market />
          <Roadmap />
          <TechDemo />
          <FounderVideo />
          <Investment />
          <InvestNow />
          <FutureVision />
          <About />
          <Updates />
          <Contact />
        </main>
        <Footer />
        <Toaster theme="dark" position="bottom-right" />
      </div>
    );
  }

  return (
    <>
      {page}
      <CookieConsent />
    </>
  );
}
